/* eslint-disable react/no-unknown-property */
import React, { useEffect, useState } from "react";
import * as R from "ramda";
import { FullPageSpinner } from "components/Spinner";
import { PMList } from "components/PaymentMethods/PMList";
import { ENV } from "env";
import { PRODUCTION } from "utils/constants";
import { isTrue, isNilOrEmpty, useSessionJWT, useSessionCtx } from "utils";
import * as paymentsApi from "api/payments";
import * as accountsCustomerApi from "api/accounts-customer";
import { Helmet } from "react-helmet";
import { useSettings } from "utils/settingsContext";

const DEFAUT_FNET_TIMEOUT_MS = 2000;

const isPaypal = R.propEq("provider", "paypal");
const isSandbox = !R.equals(PRODUCTION, ENV);

const getFinalCallbackTimeout = (appSettings) =>
  R.defaultTo(DEFAUT_FNET_TIMEOUT_MS, appSettings?.fraudnet?.callbackTimeoutMS);

export const Fraudnet = ({ onComplete, takeSessionFromCtx = false, noFullpageSpinner = false }) => {
  const { debug, sessionId } = useSessionCtx();
  debug("fraudnet ON");

  const { sessionJWT } = useSessionJWT();
  const { appSettings, integratorSettings } = useSettings();
  const [callbacksSet, setCallbacksSet] = useState(false);
  const [callbackCount, setCallbackCount] = useState(0);
  const [session, setSession] = useState(null);
  const fraudnetCallbackTimeout = getFinalCallbackTimeout(appSettings);

  // On-mount, generate a paypal session with a session_id either generated client side
  // or through the getPaypalSession API call, and a source_id fetched from Redis
  useEffect(() => {
    const generatePaypalSession = async () => {
      let draftSession = {};
      if (!takeSessionFromCtx) {
        debug("grabbing paypal session from payments api");
        const response = await paymentsApi.getPaypalSession(sessionJWT);
        debug("paypal session response:", response);
        draftSession = response?.payload;
      } else {
        debug("using session id from session ctx");
        draftSession = {
          session_id: sessionId,
          source_id: integratorSettings.paypal.sourceID,
        };
      }

      debug("final paypal session:", draftSession);
      setSession(draftSession);
    };

    generatePaypalSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the session state is updated, set the callback functions on global window
  // object and count the number of callbacks initialized
  useEffect(() => {
    if (isNilOrEmpty(session)) return;
    window.fnet_cb1 = function () {
      debug("fnet_cb1");
      setCallbackCount(R.inc);
    };
    window.fnet_cb2 = function () {
      debug("fnet_cb2");
      setCallbackCount(R.inc);
    };
    setCallbacksSet(true);
    return () => {
      delete window.fnet_cb1;
      delete window.fnet_cb2;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // When the callback count updates, wait for it to be more than 1 and trigger a
  // call to onComplete (TODO: Is there a better way to deal with this?)
  useEffect(() => {
    if (callbackCount > 1) {
      onComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callbackCount]);

  // If the callback takes longer than our given fraudnet callback timeout setting
  // in Redis, we cancel out here and call onComplete anyway
  useEffect(() => {
    if (isNilOrEmpty(session)) return;

    const timeoutId = setTimeout(async () => {
      await accountsCustomerApi.logError(sessionJWT, { flowLoc: "PAYPAL_FNET_TIMEOUT", session });
      onComplete();
    }, fraudnetCallbackTimeout);

    return () => clearTimeout(timeoutId);
  }, [onComplete, sessionJWT, session, fraudnetCallbackTimeout]);

  return (
    <>
      {callbacksSet && (
        <>
          <Helmet>
            <script
              type="application/json"
              fncls="fnparams-dede7cc5-15fd-4c75-a9f4-36c430ee3a99"
            >{`{
        "f":"${session.session_id}",
        "s":"${session.source_id}",
        "cb1": "fnet_cb1",
        "cb2": "fnet_cb2",
        "sandbox": "${isSandbox}"
    }`}</script>
            <script type="text/javascript" src="https://c.paypal.com/da/r/fb.js"></script>
          </Helmet>
          <span data-testid="fnet-scripts" />
        </>
      )}
      {!takeSessionFromCtx && !noFullpageSpinner ? (
        <FullPageSpinner data-testid="fnet-spinner" />
      ) : null}
    </>
  );
};

export const shouldInvokeFraudnet = (selected, appSettings) => {
  const fraudnetFeatureFlagOn = isTrue(appSettings?.feature_flags?.fraudnet);
  return fraudnetFeatureFlagOn && isPaypal(selected);
};

export const FraudnetPMList = ({
  paymentMethods,
  balances,
  onSelect,
  onReady,
  chooserConfig,
  allowedProviders = {},
  showMenu,
  onComplete,
}) => {
  const { appSettings } = useSettings();
  const [selected, setSelected] = useState(null);
  const [fraudnetOn, setFraudnetOn] = useState(false);

  const handleSelect = (selected) => {
    setSelected(selected);
    shouldInvokeFraudnet(selected, appSettings) ? setFraudnetOn(true) : onSelect(selected);
  };

  const handleFraudnetComplete = () => onSelect(selected);

  if (fraudnetOn) {
    return <Fraudnet onComplete={handleFraudnetComplete} />;
  }
  return (
    <PMList
      paymentMethods={paymentMethods}
      balances={balances}
      onSelect={handleSelect}
      onReady={onReady}
      chooserConfig={chooserConfig}
      allowedProviders={allowedProviders}
      showMenu={showMenu}
      onComplete={onComplete}
    />
  );
};
